import DataTable, { useDataTableHandle } from '../../DataTable';
import { ExternalLinkAlt } from '@styled-icons/fa-solid';
import { Link } from 'react-router-dom';
import { useEffect, useState, useContext } from 'react';
import { postRegistrationListLazy } from '../../apiCalls';
import { getRegistrationStatusList } from '../../../api/epoApplicationsFilters';
import isoDateConverter from '../../isoDateConverter';
import { useSecurity } from '../../Security/security';
import { StoreContext } from '../../Store';
import EpoApplicationsFilters from './EpoApplicationsFilters';
import ResetButton from '../../Buttons/ResetButton';

const EpoApplications = () => {
  const [epoApplications, setEpoApplications] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const { validateRole } = useSecurity();
  const [paginationLoaded, setPaginationLoaded] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const store = useContext(StoreContext);

  const dth = useDataTableHandle();

  const persistedFilters = store.epoApplicationsFilters[0]?.filters || {};
  const hasFiltersApplied =
    !!Object.keys(persistedFilters).length ||
    (dth?.current?.sortStatus?.currentKey && Object.keys(dth.current.sortStatus).length);

  const buildFilterPayload = (filters) => {
    const f = filters || persistedFilters;
    const str = (v) => (v != null && v !== '' ? String(v) : null);
    return {
      reference: str(f.reference),
      companyName: str(f.companyName),
      eoriNumber: str(f.eoriNumber),
      originator: str(f.originator),
      submitter: str(f.submitter),
      Status: str(f.status),
      lastUpdatedFrom: str(f.lastModifiedFrom),
      lastUpdatedTo: str(f.lastModifiedTo),
      createdFrom: str(f.createdDateFrom),
      createdTo: str(f.createdDateTo),
    };
  };

  async function fetchData(newFilters, goToFirstPage = false, resetSort = false) {
    try {
      setIsLoading(true);
      const pagination = dth.current.pagination;
      const sortStatus = resetSort ? {} : dth.current.sortStatus;
      const filtersToSend = buildFilterPayload(newFilters);
      const hasFilters = Object.values(filtersToSend).some((v) => v != null && v !== '');

      const payload = {
        skip: goToFirstPage ? 0 : pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus?.currentKey,
        sort_order: sortStatus?.direction ?? 'desc',
        filter: filtersToSend,
        applyFilters: hasFilters ? 1 : 0,
      };

      const data = await postRegistrationListLazy(payload);
      dth.current.setServerDataLength(data.totalItems);
      setEpoApplications(data.data);
      const statusList = await getRegistrationStatusList();
      const list = Array.isArray(statusList) ? statusList : (statusList?.data ?? statusList?.results ?? []);
      setStatuses(
        list
          .filter((s) => (s.status || s.data) != null)
          .map((s) => ({ id: s.status || s.data, data: s.status || s.data }))
      );
      if (goToFirstPage) dth.current.resetPagination();
    } catch (err) {
      setRequestFailed(true);
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (hasFiltersApplied) {
      setFilterData({ ...persistedFilters });
      setShowFilters(true);
    }
  }, []);

  useEffect(() => {
    if (paginationLoaded) fetchData();
  }, [paginationLoaded]);

  async function updateData(setPaginationLength, pagination, sortStatus, search) {
    try {
      const filtersToSend = buildFilterPayload();
      const hasFilters = Object.values(filtersToSend).some((v) => v != null && v !== '');
      const data = await postRegistrationListLazy({
        skip: pagination.pageNo * pagination.itemsPerPage,
        amount: pagination.itemsPerPage,
        sort_by: sortStatus?.currentKey,
        sort_order: sortStatus?.direction ?? 'desc',
        search,
        filter: filtersToSend,
        applyFilters: hasFilters ? 1 : 0,
      });
      setPaginationLength(data.totalItems);
      setEpoApplications(data.data);
    } catch (err) {
      setRequestFailed(true);
      console.log(err);
    }
  }

  const handleReset = async () => {
    dth.current.resetPagination();
    dth.current.setSortStatus?.({});
    store.epoApplicationsFilters[1]({});
    setFilterData({});
    await fetchData({}, true, true);
  };

  const handleSubmitFilters = async () => {
    store.epoApplicationsFilters[1]({ filters: { ...filterData } });
    await fetchData(filterData, true);
  };

  if (requestFailed) return <p>Failed!</p>;

  return (
    <div className="full-width-container">
      <section className="floating-box" style={{ maxWidth: '1500px' }}>
        <div className="d-flex justify-content-between align-items-center px-3 pt-4">
          <h1 className="page-heading">Customer Applications</h1>
          <div className="d-flex gap-4 align-items-center flex-shrink-0">
            {hasFiltersApplied && <ResetButton onClick={handleReset} inline />}
            <button
              className="stepper-button"
              onClick={() => setShowFilters(!showFilters)}
              style={{ minWidth: 145, maxWidth: 145 }}
              disabled={hasFiltersApplied && showFilters}
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>
        </div>

        <EpoApplicationsFilters
          visible={showFilters}
          values={filterData}
          onChange={setFilterData}
          onSubmit={handleSubmitFilters}
        />

        <div className="px-4 pt-3">
          <DataTable
            idKey="id"
            data={epoApplications}
            handle={dth}
            className="table-striped"
            filterable={false}
            sortable
            lazyLoading
            advancedSearch
            onUpdate={updateData}
            showAdvnSearch={false}
            onPaginationLoaded={() => {
              setPaginationLoaded(true);
            }}
            isLoading={isLoading}
          >
            <DataTable.Headers
              className=" px-2"
              style={{
                fontSize: '14.4px',
                backgroundColor: '#fff',
                paddingBottom: '10px',
                borderBottom: '1px solid #ddd',
                borderTop: '1px solid #ddd',
              }}
            />
            <DataTable.Pagination itemsPerPage={[10, 25, 50]} className="w-100 d-flex justify-content-end" />
            <DataTable.Column colKey="reference" header="Reference" searchOpts={{ type: 'text', sendAs: 'refno' }} />
            <DataTable.Column
              colKey="companyName"
              header="Company Name"
              searchOpts={{ type: 'select', src: 'data', sendAs: 'compName' }}
            >
              {(item) => {
                return item.companyName?.slice(0, 20) + (item.companyName?.length > 20 ? '...' : '');
              }}
            </DataTable.Column>
            <DataTable.Column
              colKey="eoriNumber"
              header="Eori Number"
              searchOpts={{ type: 'text', sendAs: 'eoriNo' }}
            />
            <DataTable.Column colKey="originator" header="Originator" searchOpts={{ type: 'text', sendAs: 'origin' }} />
            <DataTable.Column colKey="submitter" header="Submitter Name" />
            <DataTable.Column
              colKey="Status"
              header="Status"
              searchOpts={{ type: 'select', src: statuses, sendAs: 'status' }}
            />
            <DataTable.Column colKey="createdDate" header="created Date">
              {(item) => item?.createdDate !== null && isoDateConverter(item?.createdDate)}
            </DataTable.Column>
            <DataTable.Column colKey="lastModified" header="Last Updated">
              {(item) => {
                return isoDateConverter(item.lastModified);
              }}
            </DataTable.Column>
            {(validateRole({ department: 'OPS' }) ||
              validateRole({ department: 'FIN' }) ||
              validateRole({ department: 'CMT' }) ||
              validateRole({ department: 'QA' })) && (
              <DataTable.Column
                header="View"
                style={{ height: '1px', padding: 0 }}
                headerStyle={{ textAlign: 'center' }}
              >
                {(item) => {
                  return (
                    <Link
                      className="external-table-link"
                      to={{ pathname: `/EpoApplicationDetails/${item.id}`, search: `?refNo=${item.reference}` }}
                    >
                      <ExternalLinkAlt width="1.1rem" height="1.1rem" />
                    </Link>
                  );
                }}
              </DataTable.Column>
            )}
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default EpoApplications;
